/**
 * Video Board page — simulates a NEVCO Stadium Pro video board.
 * Receives program output from the main sim tab via BroadcastChannel.
 *
 * On image load, scans the PNG for the transparent bounding box to
 * auto-detect the screen opening position. The screen is 16:9 (1060x596
 * in the 1920x1080 source image).
 */

import { onProgramUpdate, onProgramClear, onVideoSync, onLessonComplete } from '../engine/videoBoardChannel';

let screenEl: HTMLElement;
let idleLogo: HTMLElement;
let programContent: HTMLElement;
let frameImg: HTMLImageElement;

// Preloaded video elements keyed by src URL — start buffering before program-update arrives
const preloadedVideos = new Map<string, HTMLVideoElement>();

// Transparent area bounds as fractions of image size (detected on load)
let screenBounds = { left: 0, top: 0, width: 0, height: 0 };

export function renderVideoBoard(container: HTMLElement): void {
  container.innerHTML = `
    <div class="vb-wrapper">
      <img class="vb-frame" src="/assets/enterprise/Video_Board.png" alt="NEVCO Stadium Pro">
      <div class="vb-screen">
        <div class="vb-idle-logo">
          <img src="/assets/enterprise/nevco-logo.png" alt="NEVCO">
        </div>
        <div class="vb-program-content"></div>
      </div>
    </div>
    <div class="vb-toolbar">
      <button class="vb-fullscreen-btn" title="Toggle Fullscreen (F11)">⛶ Fullscreen</button>
      <button class="vb-close-btn" title="Close Video Board">✕ Close</button>
    </div>
  `;

  screenEl = container.querySelector('.vb-screen')!;
  idleLogo = container.querySelector('.vb-idle-logo')!;
  programContent = container.querySelector('.vb-program-content')!;
  frameImg = container.querySelector('.vb-frame') as HTMLImageElement;

  // Detect transparent area and position screen once image loads
  const onLoad = () => {
    detectTransparentBounds();
    positionScreen();
  };
  if (frameImg.complete && frameImg.naturalWidth > 0) {
    onLoad();
  }
  frameImg.addEventListener('load', onLoad);

  // Reposition on resize (fullscreen, window resize)
  window.addEventListener('resize', () => {
    positionScreen();
    if (programContent.style.display !== 'none') {
      requestAnimationFrame(() => rescaleTemplates());
    }
  });

  // Toolbar actions
  container.querySelector('.vb-fullscreen-btn')!.addEventListener('click', () => {
    if (!document.fullscreenElement) {
      container.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen();
    }
  });

  container.querySelector('.vb-close-btn')!.addEventListener('click', () => {
    window.close();
  });

  // Preload videos immediately from localStorage — start buffering before program-update
  try {
    const stored = localStorage.getItem('vb-video-urls');
    if (stored) {
      const entries: { src: string; currentTime: number }[] = JSON.parse(stored);
      for (const entry of entries) {
        const video = document.createElement('video');
        video.src = entry.src;
        video.preload = 'auto';
        video.autoplay = true;
        video.loop = true;
        video.muted = true;
        video.playsInline = true;
        video.style.display = 'none';
        document.body.appendChild(video);
        video.addEventListener('loadeddata', () => {
          video.currentTime = entry.currentTime;
          video.play().catch(() => {});
        }, { once: true });
        preloadedVideos.set(entry.src, video);
      }
    }
  } catch (_) {}

  // Listen for program updates from main sim tab
  onProgramUpdate((html, videoTimes) => {
    // Preserve already-playing videos so they don't reload on every update
    const existingVideos = new Map<string, HTMLVideoElement>();
    programContent.querySelectorAll<HTMLVideoElement>('video[src]').forEach((v) => {
      existingVideos.set(v.src, v);
      v.remove(); // detach but keep alive
    });

    // Build a lookup of video timestamps from the main sim
    const timeMap = new Map<string, number>();
    for (const vt of videoTimes) {
      timeMap.set(vt.src, vt.currentTime);
    }

    programContent.innerHTML = html;
    idleLogo.style.display = 'none';
    programContent.style.display = '';

    // Hydrate video placeholders — reuse existing videos when possible
    programContent.querySelectorAll<HTMLElement>('[data-video-src]').forEach((placeholder) => {
      const src = placeholder.getAttribute('data-video-src');
      if (!src) return;

      // Reuse an already-playing video element if we have one
      const existing = existingVideos.get(src);
      if (existing) {
        existing.style.cssText = 'width:100%;height:100%;object-fit:contain;background:#000;display:block;';
        placeholder.replaceWith(existing);
        existingVideos.delete(src);
        return;
      }

      // Use preloaded video if available (already buffered), otherwise create new
      const preloaded = preloadedVideos.get(src);
      if (preloaded) {
        preloaded.style.cssText = 'width:100%;height:100%;object-fit:contain;background:#000;display:block;';
        preloaded.style.display = 'block';
        placeholder.replaceWith(preloaded);
        preloadedVideos.delete(src);
      } else {
        const video = document.createElement('video');
        video.src = src;
        video.autoplay = true;
        video.loop = true;
        video.muted = true;
        video.playsInline = true;
        video.style.cssText = 'width:100%;height:100%;object-fit:contain;background:#000;display:block;';
        const seekTime = timeMap.get(src) ?? 0;
        video.addEventListener('loadeddata', () => {
          video.currentTime = seekTime;
          video.play().catch(() => {});
        }, { once: true });
        placeholder.replaceWith(video);
      }
    });

    requestAnimationFrame(() => rescaleTemplates());
  });

  onProgramClear(() => {
    programContent.innerHTML = '';
    programContent.style.display = 'none';
    idleLogo.style.display = '';
  });

  onLessonComplete((lessonName) => {
    // Stop all videos
    programContent.querySelectorAll<HTMLVideoElement>('video').forEach((v) => {
      v.pause();
    });
    // Show completion card on the video board screen
    programContent.innerHTML = `
      <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;text-align:center;color:#fff;font-family:sans-serif;">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="#2a9f45" style="margin-bottom:16px;">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        </svg>
        <h2 style="margin:0 0 8px;font-size:1.5rem;">Lesson Complete!</h2>
        <p style="margin:0 0 20px;color:#b0b0b0;font-size:1rem;">${lessonName}</p>
        <p style="margin:0;color:#707070;font-size:0.85rem;">Click <strong style="color:#ed7001;">✕ Close</strong> at the top right, then return to the simulator tab.</p>
      </div>
    `;
    programContent.style.display = '';
    idleLogo.style.display = 'none';

    // Pulse the Close button so the trainee knows how to get back to the sim tab.
    container.querySelector('.vb-close-btn')?.classList.add('vb-close-highlight');
  });

  // One-time sync: when a new video is created, the first sync heartbeat
  // sets its position. After that, playback runs independently at the same rate.
  const syncedVideos = new WeakSet<HTMLVideoElement>();
  onVideoSync((videoTimes) => {
    for (const vt of videoTimes) {
      programContent.querySelectorAll<HTMLVideoElement>('video').forEach((video) => {
        if (syncedVideos.has(video)) return; // already synced, leave it alone
        if (video.src !== vt.src && !video.src.endsWith(vt.src)) return;
        if (video.readyState < 2) return; // not ready to seek yet
        video.currentTime = vt.currentTime;
        syncedVideos.add(video);
      });
    }
  });

  // Start with idle state
  programContent.style.display = 'none';
  idleLogo.style.display = '';
}

/**
 * Draw the frame image to an offscreen canvas and scan for the screen
 * opening (transparent rectangle). Starts scanning from 40% down the
 * image to skip the truss arch transparency, finding only the main screen.
 */
function detectTransparentBounds(): void {
  const w = frameImg.naturalWidth;
  const h = frameImg.naturalHeight;
  if (w === 0 || h === 0) return;

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(frameImg, 0, 0);
  const data = ctx.getImageData(0, 0, w, h).data;

  const isTransparent = (x: number, y: number) =>
    data[(y * w + x) * 4 + 3] === 0;

  const cx = Math.floor(w / 2);

  // Find a seed point inside the screen: scan down center column
  // starting at 40% (below the truss arch) to find first transparent pixel
  let seedY = -1;
  for (let y = Math.floor(h * 0.4); y < h; y++) {
    if (isTransparent(cx, y)) { seedY = y; break; }
  }
  if (seedY === -1) return;

  // From seed, scan UP to find top edge of screen opening
  let top = 0;
  for (let y = seedY; y >= 0; y--) {
    if (!isTransparent(cx, y)) { top = y + 1; break; }
  }

  // From seed, scan DOWN to find bottom edge
  let bottom = h - 1;
  for (let y = seedY; y < h; y++) {
    if (!isTransparent(cx, y)) { bottom = y - 1; break; }
  }

  // Use vertical midpoint of screen for left/right scan
  const midY = Math.floor((top + bottom) / 2);

  let left = 0;
  for (let x = cx; x >= 0; x--) {
    if (!isTransparent(x, midY)) { left = x + 1; break; }
  }

  let right = w - 1;
  for (let x = cx; x < w; x++) {
    if (!isTransparent(x, midY)) { right = x - 1; break; }
  }

  // Expand by a few pixels to cover anti-aliased edges
  const pad = 3;
  left = Math.max(0, left - pad);
  top = Math.max(0, top - pad);
  right = Math.min(w - 1, right + pad);
  bottom = Math.min(h - 1, bottom + pad);

  screenBounds = {
    left: left / w,
    top: top / h,
    width: (right - left + 1) / w,
    height: (bottom - top + 1) / h,
  };
}

/**
 * Position the screen overlay using the detected transparent bounds,
 * scaled to the actual rendered image size.
 */
function positionScreen(): void {
  const iw = frameImg.clientWidth;
  const ih = frameImg.clientHeight;
  if (iw === 0 || ih === 0 || screenBounds.width === 0) return;

  const sx = iw * screenBounds.left;
  const sy = ih * screenBounds.top;
  const sw = iw * screenBounds.width;
  const sh = ih * screenBounds.height;

  screenEl.style.left   = `${sx}px`;
  screenEl.style.top    = `${sy}px`;
  screenEl.style.width  = `${sw}px`;
  screenEl.style.height = `${sh}px`;
}

/**
 * Recalculate the transform scale on each `.template-inner` to fit the
 * video board screen dimensions instead of the program canvas dimensions.
 */
function rescaleTemplates(): void {
  programContent.querySelectorAll<HTMLElement>('.template-inner').forEach((inner) => {
    const viewport = inner.closest('.template-viewport') as HTMLElement;
    if (!viewport) return;
    const rect = viewport.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    const w = parseFloat(inner.style.width) || 1920;
    const h = parseFloat(inner.style.height) || 1080;

    // Read user transform from data attributes (set by templateRenderer)
    const fitMode = inner.dataset.fitMode || 'fit';
    const userScaleX = parseFloat(inner.dataset.userScaleX || '100') / 100;
    const userScaleY = parseFloat(inner.dataset.userScaleY || '100') / 100;

    const ratioX = rect.width / w;
    const ratioY = rect.height / h;
    let sx: number, sy: number;
    switch (fitMode) {
      case 'fill':
        sx = sy = Math.max(ratioX, ratioY);
        break;
      case 'stretch':
        sx = ratioX;
        sy = ratioY;
        break;
      default: // 'fit'
        sx = sy = Math.min(ratioX, ratioY);
        break;
    }
    sx *= userScaleX;
    sy *= userScaleY;

    const posX = parseFloat(inner.dataset.posX || '0');
    const posY = parseFloat(inner.dataset.posY || '0');

    inner.style.transform = `scale(${sx}, ${sy})`;
    inner.style.transformOrigin = 'top left';
    const offsetX = (rect.width - w * sx) / 2 + (posX / 100) * rect.width;
    const offsetY = (rect.height - h * sy) / 2 + (posY / 100) * rect.height;
    inner.style.left = `${offsetX}px`;
    inner.style.top = `${offsetY}px`;
  });
}
